import { useParams, Link } from 'react-router-dom';
import Footer from '@/components/feature/Footer';
import { campaigns } from '@/mocks/campaigns';
import type { ImpactExpense, ImpactMedia } from '@/mocks/campaigns';
import SAR from '@/components/feature/SAR';

const ExpenseBar = ({ expense, total }: { expense: ImpactExpense; total: number }) => (
  <div className="flex items-center gap-4">
    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 shrink-0">
      <i className={`${expense.icon} text-emerald-600 text-base`}></i>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-stone-700">{expense.label}</span>
        <span className="text-sm font-bold text-stone-800">
          <SAR amount={expense.amount} iconSize={12} />
        </span>
      </div>
      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all duration-700"
          style={{ width: `${expense.percentage}%` }}
        />
      </div>
    </div>
    <span className="text-xs font-semibold text-emerald-600 w-10 text-left shrink-0">
      {expense.percentage}%
    </span>
  </div>
);

const MediaCard = ({ item }: { item: ImpactMedia }) => (
  <div className="rounded-xl overflow-hidden group cursor-pointer">
    <div className="relative h-52 overflow-hidden">
      <img
        src={item.url}
        alt={item.caption}
        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
      />
      {item.type === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90">
            <i className="ri-play-fill text-stone-800 text-xl mr-[-2px]"></i>
          </div>
        </div>
      )}
    </div>
    <p className="text-xs text-stone-500 mt-2 px-1">{item.caption}</p>
  </div>
);

const ImpactReportPage = () => {
  const { id } = useParams<{ id: string }>();
  const campaign = campaigns.find((c) => c.id === id) || campaigns[0];
  const report = campaign.impactReport;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (!report) {
    return (
      <div className="min-h-screen bg-stone-50" dir="rtl">
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-stone-100 mx-auto mb-6">
            <i className="ri-file-chart-line text-stone-400 text-3xl"></i>
          </div>
          <h2 className="text-2xl font-black text-stone-800 mb-3">لا يوجد تقرير أثر بعد</h2>
          <p className="text-stone-500 mb-8">لم يقم صاحب الحملة بنشر تقرير الأثر حتى الآن. تابع الحملة لتصلك الإشعارات عند نشره.</p>
          <Link
            to={`/campaign/${campaign.id}`}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line"></i>
            العودة للحملة
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const totalExpenses = report.expenses.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-100 pt-20">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-3 flex items-center gap-2 text-xs text-stone-400">
          <Link to="/" className="hover:text-emerald-600 cursor-pointer">الرئيسية</Link>
          <i className="ri-arrow-left-s-line"></i>
          <Link to="/explore" className="hover:text-emerald-600 cursor-pointer">الحملات</Link>
          <i className="ri-arrow-left-s-line"></i>
          <Link to={`/campaign/${campaign.id}`} className="hover:text-emerald-600 cursor-pointer line-clamp-1 max-w-[160px]">{campaign.title}</Link>
          <i className="ri-arrow-left-s-line"></i>
          <span className="text-stone-600">تقرير الأثر</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-10">

        {/* Hero Header */}
        <div className="relative rounded-2xl overflow-hidden mb-8 h-64 md:h-80">
          <img
            src={campaign.coverImage}
            alt={campaign.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 right-0 left-0 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-emerald-500 text-white text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1.5">
                <i className="ri-bar-chart-2-line text-xs"></i>
                تقرير الأثر
              </span>
              <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                نُشر في {formatDate(report.publishedAt)}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white leading-tight">{campaign.title}</h1>
            <div className="flex items-center gap-2 mt-2">
              <img src={campaign.creatorAvatar} alt={campaign.creatorName} className="w-7 h-7 rounded-full object-cover object-top border-2 border-white/50" />
              <span className="text-white/80 text-sm">{campaign.creatorName}</span>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: 'ri-funds-line', label: 'إجمالي التمويل', value: <SAR amount={campaign.raisedAmount} iconSize={14} />, color: 'text-emerald-600' },
            { icon: 'ri-user-heart-line', label: 'عدد الداعمين', value: `${campaign.donorsCount.toLocaleString('en-US')}+`, color: 'text-amber-600' },
            { icon: 'ri-group-line', label: 'المستفيدون', value: `${report.beneficiariesCount.toLocaleString('en-US')}+`, color: 'text-rose-500' },
            { icon: 'ri-checkbox-circle-line', label: 'نسبة الإنجاز', value: `${report.completionRate}%`, color: 'text-emerald-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-stone-100 p-5 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-50 mx-auto mb-3">
                <i className={`${stat.icon} ${stat.color} text-lg`}></i>
              </div>
              <div className={`text-xl font-black ${stat.color} mb-1`}>{stat.value}</div>
              <div className="text-xs text-stone-500">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">

            {/* Summary */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-lg font-black text-stone-800 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
                  <i className="ri-file-text-line text-emerald-600 text-sm"></i>
                </div>
                ملخص الأثر
              </h2>
              <p className="text-stone-600 leading-relaxed text-sm">{report.summary}</p>
            </div>

            {/* Outcomes */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-lg font-black text-stone-800 mb-5 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
                  <i className="ri-trophy-line text-emerald-600 text-sm"></i>
                </div>
                ما تحقق بفضلكم
              </h2>
              <div className="space-y-3">
                {report.outcomes.map((outcome, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-100 shrink-0 mt-0.5">
                      <i className="ri-check-line text-emerald-600 text-xs"></i>
                    </div>
                    <span className="text-stone-700 text-sm leading-relaxed">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Expenses */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-lg font-black text-stone-800 mb-2 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
                  <i className="ri-pie-chart-line text-emerald-600 text-sm"></i>
                </div>
                كيف صُرف المال؟
              </h2>
              <p className="text-xs text-stone-400 mb-5">
                إجمالي المصروف: <span className="font-bold text-stone-600"><SAR amount={totalExpenses} iconSize={11} /></span>
              </p>
              <div className="space-y-4">
                {report.expenses.map((expense, i) => (
                  <ExpenseBar key={i} expense={expense} total={totalExpenses} />
                ))}
              </div>
            </div>

            {/* Media Gallery */}
            {report.media.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-6">
                <h2 className="text-lg font-black text-stone-800 mb-5 flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
                    <i className="ri-image-line text-emerald-600 text-sm"></i>
                  </div>
                  صور وفيديوهات من الميدان
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {report.media.map((item, i) => (
                    <MediaCard key={i} item={item} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">

            {/* Thank You Card */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/20 mb-4">
                <i className="ri-heart-line text-white text-lg"></i>
              </div>
              <h3 className="font-black text-lg mb-3">رسالة شكر</h3>
              <p className="text-emerald-50 text-sm leading-relaxed">{report.thankYouMessage}</p>
              <div className="mt-5 pt-4 border-t border-white/20 flex items-center gap-3">
                <img
                  src={campaign.creatorAvatar}
                  alt={campaign.creatorName}
                  className="w-9 h-9 rounded-full object-cover object-top border-2 border-white/40"
                />
                <div>
                  <div className="font-semibold text-sm">{campaign.creatorName}</div>
                  <div className="text-emerald-200 text-xs">{campaign.location}</div>
                </div>
              </div>
            </div>

            {/* Campaign Info */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5">
              <h3 className="font-bold text-stone-800 text-sm mb-4">معلومات الحملة</h3>
              <div className="space-y-3">
                {[
                  { icon: 'ri-map-pin-line', label: 'الموقع', value: campaign.location },
                  { icon: 'ri-price-tag-3-line', label: 'التصنيف', value: campaign.category },
                  { icon: 'ri-calendar-check-line', label: 'تاريخ التقرير', value: formatDate(report.publishedAt) },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-50 shrink-0">
                      <i className={`${item.icon} text-stone-500 text-sm`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-stone-400">{item.label}</div>
                      <div className="text-sm font-medium text-stone-700">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Share Report */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5">
              <h3 className="font-bold text-stone-800 text-sm mb-3">شارك تقرير الأثر</h3>
              <p className="text-xs text-stone-500 mb-4">ساعد في نشر قصة النجاح هذه</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: 'ri-whatsapp-line', label: 'واتساب', color: 'text-green-600 bg-green-50 hover:bg-green-100' },
                  { icon: 'ri-twitter-x-line', label: 'تويتر', color: 'text-stone-800 bg-stone-50 hover:bg-stone-100' },
                  { icon: 'ri-snapchat-line', label: 'سناب', color: 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100' },
                  { icon: 'ri-link', label: 'نسخ الرابط', color: 'text-stone-600 bg-stone-50 hover:bg-stone-100' },
                ].map((s) => (
                  <button
                    key={s.label}
                    className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${s.color}`}
                  >
                    <i className={`${s.icon} text-sm`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Back to Campaign */}
            <Link
              to={`/campaign/${campaign.id}`}
              className="flex items-center justify-center gap-2 w-full bg-stone-800 text-white py-3.5 rounded-xl font-semibold text-sm hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-line"></i>
              العودة لصفحة الحملة
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ImpactReportPage;
